import { baseApi } from "./baseApi";

// per-code default delivery policy (mirrors the messenger NotificationDef shape)
export interface NotifDefaultDeliveryPolicy {
  primary: "tg" | "push";
  fallback?: "tg" | "push" | null;
}

// one auto-registered notification code from the messenger registry
export interface NotifDef {
  _id?: string;
  code: string;
  serviceClientId?: string;
  displayName?: string;
  defaultEmoji?: string | null;
  description?: string | null;
  defaultDeliveryPolicy?: NotifDefaultDeliveryPolicy | null;
  firstSeenAt?: string;
  lastSeenAt?: string;
  sendCount?: number;
  skipCount?: number;
}

export interface NotifDefsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface NotifDefsResponse {
  defs: NotifDef[];
  pagination: NotifDefsPagination;
}

// a recent send sample (messenger returns [] until history carries the code)
export interface NotifSample {
  timestamp?: string;
  recipient?: string;
  status?: string;
  message?: string;
  statusDetail?: string;
  source?: string;
}

export interface NotifDefDetailResponse {
  def: NotifDef;
  samples: NotifSample[];
}

export interface NotifMute {
  _id?: string;
  code: string;
  mutedBy?: string;
  mutedAt?: string;
  reason?: string | null;
}

export interface UpdateNotifDefBody {
  displayName?: string;
  defaultEmoji?: string | null;
  description?: string | null;
  defaultDeliveryPolicy?: NotifDefaultDeliveryPolicy | null;
}

export interface SetServicePolicyBody {
  serviceClientId: string;
  // omit primary (send {}) to clear the policy back to fan-out
  primary?: "tg" | "push";
  fallback?: "tg" | "push" | null;
}

// chat notification style (mirrors the backend globalNotificationConfig enum)
export type ChatNotificationStyle = "sender_title" | "single_line";

export interface ChatStyleResponse {
  success: boolean;
  style: ChatNotificationStyle;
  styles: ChatNotificationStyle[];
}

export interface ChatStyleTestBody {
  style?: ChatNotificationStyle;
  channel: "general" | "personal";
}

export interface ChatStyleTestResponse {
  success: boolean;
  message?: string;
  data?: { style: ChatNotificationStyle; channel: string; message: string; pushTitle: string | null };
}

// global voice-message config (fun summary on/off + the editable tone prompt)
export interface VoiceConfig {
  funSummaryEnabled: boolean;
  funSummaryPrompt: string;
}

export interface VoiceConfigResponse {
  success: boolean;
  config: VoiceConfig;
  defaultPrompt: string;
}

export interface VoiceConfigTestBody {
  transcript: string;
  funSummaryPrompt?: string;
  funSummaryEnabled?: boolean;
}

export interface VoiceConfigTestResponse {
  success: boolean;
  data?: { cleaned: string; summary: string; funSummary: string; funSummaryEnabled: boolean };
}

// voice activity log — one row per processed voice note (chat + coffee)
export type VoiceSurface = "chat_group" | "chat_personal" | "coffee";

export interface VoiceLogEvent {
  _id: string;
  surface: VoiceSurface;
  voiceId: string;
  username: string;
  userId?: string;
  rawTranscript: string;
  cleaned: string;
  summary: string;
  funSummary: string;
  funSummaryEnabled: boolean;
  transcribeModel: string;
  cleanupModel: string;
  language: string;
  durationMs?: number;
  audioUrl: string;
  createdAt: string;
}

export interface VoiceLogsResponse {
  success: boolean;
  events: VoiceLogEvent[];
  hasMore: boolean;
  surfaces: VoiceSurface[];
}

export const notificationsAdminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotifDefs: builder.query<NotifDefsResponse, { page?: number; limit?: number } | void>({
      query: (arg) => ({
        url: "/admin/notifications/defs",
        params:
          arg && typeof arg === "object"
            ? {
                ...(arg.page != null ? { page: arg.page } : {}),
                ...(arg.limit != null ? { limit: arg.limit } : {}),
              }
            : {},
      }),
      providesTags: ["Notif"],
    }),
    getNotifDef: builder.query<NotifDefDetailResponse, string>({
      query: (code) => `/admin/notifications/defs/${encodeURIComponent(code)}`,
      providesTags: ["Notif"],
    }),
    updateNotifDef: builder.mutation<NotifDef, { code: string; patch: UpdateNotifDefBody }>({
      query: ({ code, patch }) => ({
        url: `/admin/notifications/defs/${encodeURIComponent(code)}`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: ["Notif"],
    }),
    getNotifMutes: builder.query<NotifMute[], void>({
      query: () => "/admin/notifications/mutes",
      providesTags: ["NotifMute"],
    }),
    muteNotif: builder.mutation<NotifMute, { code: string; reason?: string }>({
      query: ({ code, reason }) => ({
        url: "/admin/notifications/mutes",
        method: "POST",
        body: { code, reason },
      }),
      invalidatesTags: ["Notif", "NotifMute"],
    }),
    unmuteNotif: builder.mutation<{ success: boolean; removed: boolean }, string>({
      query: (code) => ({
        url: `/admin/notifications/mutes/${encodeURIComponent(code)}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Notif", "NotifMute"],
    }),
    setServicePolicy: builder.mutation<
      { success: boolean; data: { serviceClientId: string; deliveryPolicy: NotifDefaultDeliveryPolicy | null } },
      SetServicePolicyBody
    >({
      query: ({ serviceClientId, primary, fallback }) => ({
        url: `/admin/notifications/services/${encodeURIComponent(serviceClientId)}/policy`,
        method: "PATCH",
        // empty body (no primary) clears the policy back to fan-out
        body: primary ? { primary, fallback: fallback ?? null } : {},
      }),
    }),
    getChatNotificationStyle: builder.query<ChatStyleResponse, void>({
      query: () => "/admin/notifications/chat-style",
      providesTags: ["ChatStyle"],
    }),
    setChatNotificationStyle: builder.mutation<
      { success: boolean; style: ChatNotificationStyle },
      ChatNotificationStyle
    >({
      query: (style) => ({
        url: "/admin/notifications/chat-style",
        method: "PUT",
        body: { style },
      }),
      invalidatesTags: ["ChatStyle"],
    }),
    sendChatStyleTest: builder.mutation<ChatStyleTestResponse, ChatStyleTestBody>({
      query: (body) => ({
        url: "/admin/notifications/chat-style/test",
        method: "POST",
        body,
      }),
    }),
    getVoiceConfig: builder.query<VoiceConfigResponse, void>({
      query: () => "/admin/voice-config",
      providesTags: ["VoiceConfig"],
    }),
    setVoiceConfig: builder.mutation<{ success: boolean; config: VoiceConfig }, Partial<VoiceConfig>>({
      query: (body) => ({ url: "/admin/voice-config", method: "PUT", body }),
      invalidatesTags: ["VoiceConfig"],
    }),
    testVoiceConfig: builder.mutation<VoiceConfigTestResponse, VoiceConfigTestBody>({
      query: (body) => ({ url: "/admin/voice-config/test", method: "POST", body }),
    }),
    getVoiceLogs: builder.query<VoiceLogsResponse, { limit?: number; surface?: VoiceSurface } | void>({
      query: (arg) => ({
        url: "/admin/voice-logs",
        params: {
          limit: (arg && typeof arg === "object" && arg.limit) || 50,
          ...(arg && typeof arg === "object" && arg.surface ? { surface: arg.surface } : {}),
        },
      }),
      providesTags: ["VoiceConfig"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetNotifDefsQuery,
  useGetNotifDefQuery,
  useLazyGetNotifDefQuery,
  useUpdateNotifDefMutation,
  useGetNotifMutesQuery,
  useMuteNotifMutation,
  useUnmuteNotifMutation,
  useSetServicePolicyMutation,
  useGetChatNotificationStyleQuery,
  useSetChatNotificationStyleMutation,
  useSendChatStyleTestMutation,
  useGetVoiceConfigQuery,
  useSetVoiceConfigMutation,
  useTestVoiceConfigMutation,
  useGetVoiceLogsQuery,
} = notificationsAdminApi;
